import { makeObservable, observable, action, computed } from 'mobx';
import { trackAction } from './analytics';

export const LIBRARY_HOME_KEY = 'library-home';

/** Approximate module width + padding for smart panning; keeps clicked module visible next to sidebar */
const MODULE_VISIBILITY_BUFFER = 300;

export type SourcePanelMode = 'select' | 'edit' | 'browse';

export interface SelectModeContext {
  targetCardIds: string[];
  targetModuleId: string;
  sourceId: string;
  isMultiModule: boolean;
}

export interface EditModeContext {
  sourceId: string;
  focusRowId?: string;
}

export class UiLibrarySlideoutStore {
  sourceWidths: Map<string, number> = new Map();
  fullScreenSourceWidths: Map<string, number> = new Map();
  defaultWidth = 480; // Default width — narrower for non-modal side-by-side
  lastSourceId: string | null = null;
  lastCloseTime: number | null = null;
  isOpen = false; // Track whether the library slide-out is currently open

  // Mode state for Select vs Edit vs Browse views
  // 'browse' = original drawer behavior (navigation between sources)
  // 'select' = compact picker for empty modules
  // 'edit' = full-screen for connected modules
  mode: SourcePanelMode = 'browse';
  selectModeContext: SelectModeContext | null = null;
  editModeContext: EditModeContext | null = null;

  // Horizontal click position for smart panning (viewport-relative clientX)
  clickX: number | null = null;

  // Row highlighting for "Reveal in Library"
  highlightedRowId: string | null = null;
  private highlightTimerId: ReturnType<typeof setTimeout> | null = null;

  private readonly MEMORY_TIMEOUT_MS = 30 * 1000; // 30 seconds

  constructor() {
    makeObservable(this, {
      sourceWidths: observable,
      fullScreenSourceWidths: observable,
      defaultWidth: observable,
      lastSourceId: observable,
      lastCloseTime: observable,
      isOpen: observable,
      mode: observable,
      selectModeContext: observable,
      editModeContext: observable,
      clickX: observable,
      highlightedRowId: observable,
      currentSidebarWidth: computed,
      panAmount: computed,
      setSourceWidth: action,
      getSourceWidth: action,
      resetSourceWidth: action,
      setDefaultWidth: action,
      setLastSource: action,
      setLastCloseTime: action,
      getLastSourceIfValid: action,
      setIsOpen: action,
      openForSelection: action,
      openForEdit: action,
      openForBrowse: action,
      switchToEditMode: action,
      setHighlightedRowId: action,
      clearHighlightedRow: action,
      close: action,
      setFullScreenSourceWidth: action,
      getFullScreenSourceWidth: action,
      dispose: action,
    });

    // Load saved widths from localStorage
    this.loadWidthsFromStorage();
    this.loadFullScreenWidthsFromStorage();
  }

  /**
   * Clean up timers and resources when store is disposed
   */
  dispose() {
    if (this.highlightTimerId !== null) {
      clearTimeout(this.highlightTimerId);
      this.highlightTimerId = null;
    }
  }

  setSourceWidth(sourceId: string, width: number) {
    this.sourceWidths.set(sourceId, width);
    this.saveWidthsToStorage();
  }

  getSourceWidth(sourceId: string): number {
    return this.sourceWidths.get(sourceId) || this.defaultWidth;
  }

  /** Actual width of the currently-open sidebar (respects per-source resize). */
  get currentSidebarWidth(): number {
    if (!this.isOpen) return 0;
    if (this.lastSourceId) {
      return this.sourceWidths.get(this.lastSourceId) || this.defaultWidth;
    }
    return this.defaultWidth;
  }

  /** Pan offset for smart panning (keeps clicked module visible next to sidebar). */
  get panAmount(): number {
    const w = this.currentSidebarWidth;
    const x = this.clickX;
    if (w === 0 || x === null) return 0;
    return Math.max(
      0,
      Math.min(
        w,
        x - (typeof window !== 'undefined' ? window.innerWidth - w : 0) + MODULE_VISIBILITY_BUFFER,
      ),
    );
  }

  resetSourceWidth(sourceId: string) {
    this.sourceWidths.delete(sourceId);
    this.saveWidthsToStorage();
  }

  setDefaultWidth(width: number) {
    this.defaultWidth = width;
    this.saveWidthsToStorage();
  }

  private loadWidthsFromStorage() {
    try {
      const saved = localStorage.getItem('librarySlideoutWidths');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.sourceWidths = new Map(Object.entries(parsed));
      }

      const defaultWidth = localStorage.getItem('librarySlideoutDefaultWidth');
      if (defaultWidth) {
        this.defaultWidth = parseInt(defaultWidth, 10);
      }
    } catch (error) {
      console.warn('Failed to load library slideout widths from storage:', error);
    }
  }

  private saveWidthsToStorage() {
    try {
      const widthsObject = Object.fromEntries(this.sourceWidths);
      localStorage.setItem('librarySlideoutWidths', JSON.stringify(widthsObject));
      localStorage.setItem('librarySlideoutDefaultWidth', this.defaultWidth.toString());
    } catch (error) {
      console.warn('Failed to save library slideout widths to storage:', error);
    }
  }

  setLastSource(sourceId: string) {
    this.lastSourceId = sourceId;
  }

  setLastCloseTime() {
    this.lastCloseTime = Date.now();
  }

  getLastSourceIfValid(): string | null {
    if (!this.lastSourceId || !this.lastCloseTime) {
      return null;
    }

    const timeSinceClose = Date.now() - this.lastCloseTime;
    if (timeSinceClose > this.MEMORY_TIMEOUT_MS) {
      // Reset the memory after timeout
      this.lastSourceId = null;
      this.lastCloseTime = null;
      return null;
    }

    return this.lastSourceId;
  }

  setIsOpen(isOpen: boolean) {
    this.isOpen = isOpen;
  }

  /**
   * Open the panel in Select Mode for choosing a row to connect to card(s)
   */
  openForSelection(
    cardIds: string[],
    moduleId: string,
    sourceId: string,
    isMultiModule: boolean,
    clickX?: number,
  ) {
    // Only count mode transitions while the panel is already open — otherwise
    // the same gesture also emits library_opened (funnel inflation).
    if (this.isOpen && this.mode !== 'select') trackAction('library_mode_changed', 'select');
    this.mode = 'select';
    this.selectModeContext = {
      targetCardIds: cardIds,
      targetModuleId: moduleId,
      sourceId,
      isMultiModule,
    };
    this.editModeContext = null;
    this.clickX = clickX ?? null;
    this.isOpen = true;
    this.setLastSource(sourceId);
  }

  /**
   * Open the panel in Edit Mode for viewing/editing source data
   */
  openForEdit(sourceId: string, focusRowId?: string) {
    if (this.isOpen && this.mode !== 'edit') trackAction('library_mode_changed', 'edit');
    this.mode = 'edit';
    this.editModeContext = {
      sourceId,
      focusRowId,
    };
    this.selectModeContext = null;
    this.isOpen = true;
    this.setLastSource(sourceId);
  }

  /**
   * Open the panel in Browse Mode (original drawer behavior)
   * Used for general library browsing, not specific module selection
   */
  openForBrowse(sourceId?: string, clickX?: number) {
    if (this.isOpen && this.mode !== 'browse') trackAction('library_mode_changed', 'browse');
    this.mode = 'browse';
    this.selectModeContext = null;
    this.editModeContext = null;
    this.clickX = clickX ?? null;
    this.isOpen = true;
    if (sourceId) {
      this.setLastSource(sourceId);
    }
  }

  /**
   * Switch from Select Mode to Edit Mode (preserving source context)
   */
  switchToEditMode() {
    if (this.selectModeContext) {
      const { sourceId } = this.selectModeContext;
      this.mode = 'edit';
      this.editModeContext = { sourceId };
      this.selectModeContext = null;
    }
  }

  /**
   * Set the highlighted row ID for "Reveal in Library"
   */
  setHighlightedRowId(rowId: string | null) {
    // Cancel any existing timer to prevent race conditions
    if (this.highlightTimerId !== null) {
      clearTimeout(this.highlightTimerId);
      this.highlightTimerId = null;
    }

    this.highlightedRowId = rowId;

    // Auto-clear after 3 seconds
    if (rowId) {
      this.highlightTimerId = setTimeout(() => {
        if (this.highlightedRowId === rowId) {
          this.clearHighlightedRow();
        }
        this.highlightTimerId = null;
      }, 3000);
    }
  }

  /**
   * Clear the highlighted row
   */
  clearHighlightedRow() {
    this.highlightedRowId = null;
  }

  /**
   * Close the panel and reset mode state
   */
  close() {
    this.isOpen = false;
    this.clickX = null;
    this.setLastCloseTime();
    this.clearHighlightedRow();
    // Keep context for potential reopening, but mark as closed
  }

  setFullScreenSourceWidth(sourceId: string, width: number) {
    this.fullScreenSourceWidths.set(sourceId, width);
    this.saveFullScreenWidthsToStorage();
  }

  getFullScreenSourceWidth(sourceId: string): number {
    return this.fullScreenSourceWidths.get(sourceId) || this.getDefaultFullScreenWidth();
  }

  private getDefaultFullScreenWidth(): number {
    // Default to 80-85% of viewport width
    return Math.floor(window.innerWidth * 0.825);
  }

  private loadFullScreenWidthsFromStorage() {
    try {
      const saved = localStorage.getItem('libraryFullScreenWidths');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.fullScreenSourceWidths = new Map(Object.entries(parsed));
      }
    } catch (error) {
      console.warn('Failed to load library full-screen widths from storage:', error);
    }
  }

  private saveFullScreenWidthsToStorage() {
    try {
      const widthsObject = Object.fromEntries(this.fullScreenSourceWidths);
      localStorage.setItem('libraryFullScreenWidths', JSON.stringify(widthsObject));
    } catch (error) {
      console.warn('Failed to save library full-screen widths to storage:', error);
    }
  }
}
